import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Box,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://inkedupmanager.com/api';

const DepositSuccessPage = ({ cancelled = false }) => {
  const [searchParams] = useSearchParams();
  const appointmentId = searchParams.get('appointment_id');
  const sessionId = searchParams.get('session_id');

  const [loading, setLoading] = useState(!cancelled);
  const [appointmentDetails, setAppointmentDetails] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (cancelled || !appointmentId) {
      setLoading(false);
      return;
    }

    const fetchDetails = async () => {
      try {
        const response = await fetch(
          `${API_BASE_URL}/deposit/success/?appointment_id=${appointmentId}&session_id=${sessionId || ''}`
        );
        const data = await response.json();

        if (data.success) {
          setAppointmentDetails(data.appointment);
        } else {
          setError(data.error || 'Unable to load appointment details');
        }
      } catch (err) {
        setError('Unable to load appointment details. Your payment was still processed successfully.');
      } finally {
        setLoading(false);
      }
    };

    fetchDetails();
  }, [appointmentId, sessionId, cancelled]);

  if (loading) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8, textAlign: 'center' }}>
        <CircularProgress size={48} />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Confirming your payment...
        </Typography>
      </Container>
    );
  }

  // Cancelled page
  if (cancelled) {
    return (
      <Container maxWidth="sm" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <CancelIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" gutterBottom fontWeight="bold">
            Payment Cancelled
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Your deposit payment was not completed. No charges were made.
          </Typography>
          <Typography variant="body2" color="text.secondary">
            If you'd like to pay your deposit, please use the payment link provided by your artist.
          </Typography>
        </Paper>
      </Container>
    );
  }

  // Success page
  return (
    <Container maxWidth="sm" sx={{ mt: 8 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
        <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Deposit Received!
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          Your deposit has been successfully processed. Thank you!
        </Typography>

        {error && (
          <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
            {error}
          </Alert>
        )}

        {appointmentDetails && (
          <Card variant="outlined" sx={{ mt: 3, textAlign: 'left' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom fontWeight="bold">
                Appointment Details
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <PersonIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                <Typography variant="body1">
                  <strong>Artist:</strong> {appointmentDetails.artist_name}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <CalendarTodayIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                <Typography variant="body1">
                  <strong>Date:</strong> {appointmentDetails.date}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <AccessTimeIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                <Typography variant="body1">
                  <strong>Time:</strong> {appointmentDetails.time}
                </Typography>
              </Box>

              {appointmentDetails.deposit_amount && (
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <AttachMoneyIcon sx={{ color: 'text.secondary', mr: 1.5, fontSize: 20 }} />
                  <Typography variant="body1">
                    <strong>Deposit Paid:</strong> ${parseFloat(appointmentDetails.deposit_amount).toFixed(2)}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          A confirmation will be reflected in your artist's records. If you have questions, please contact your artist directly.
        </Typography>
      </Paper>
    </Container>
  );
};

export default DepositSuccessPage;